import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { Separator } from "@/frontend/components/ui/separator";
import { orpc } from "@/frontend/orpc-client";
import { cn } from "@/frontend/lib/utils";
import { destinationTypes, initialFormDrafts } from "@/frontend/utils/dashboard/settings/export";
import { AddDestinationDialog } from "./AddDestinationDialog";
import type { DestinationDialogType, FormDrafts } from "@/frontend/types/dashboard/settings/export";
import { DESTINATION_LOGOS } from "@/frontend/types/dashboard/settings/activity";
import { DestinationCard } from "./DestinationCard";
import { DestinationDetailView } from "./DestinationDetailView";
import { useExportEvents } from "@/frontend/hooks/useExportEvents";
import { Route } from "@/frontend/routes/dashboard/settings";

export function DestinationsSection() {
  useExportEvents();

  const { destinationId } = Route.useSearch();
  const navigate = useNavigate();

  const destinations = useQuery(orpc.admin.listExportDestinations.queryOptions({ input: {} }));

  const allDests = (destinations.data ?? []) as any[];

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<DestinationDialogType | null>(null);
  const [formDrafts, setFormDrafts] = useState<FormDrafts>(initialFormDrafts);

  if (destinationId) {
    return <DestinationDetailView destinationId={destinationId} />;
  }

  const openDialog = (type: DestinationDialogType) => {
    setDialogType(type);
    setDialogOpen(true);
  };

  const clearDraftAndClose = () => {
    if (dialogType) {
      setFormDrafts((prev) => ({
        ...prev,
        [dialogType]: initialFormDrafts()[dialogType],
      }));
    }
    setDialogOpen(false);
    void destinations.refetch();
  };

  return (
    <div className="flex flex-col gap-8">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-semibold">Export</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Configure where your data gets exported to.
          </p>
        </div>
      </div>

      <Separator />

      {/* Add Destination grid */}
      <section>
        <h3 className="text-sm font-semibold mb-3">Add Export Destination</h3>
        <div className="grid grid-cols-2 gap-4">
          {destinationTypes.map((dt) => (
            <button
              key={dt.type}
              onClick={() => openDialog(dt.type)}
              className={cn(
                "flex items-center gap-4 p-5",
                "rounded-lg border border-border",
                "hover:border-blue-500/50",
                "hover:bg-blue-500/5",
                "transition-colors text-left"
              )}
            >
              <img
                src={DESTINATION_LOGOS[dt.type] ?? "/assets/aws.png"}
                alt={dt.label}
                className="w-8 h-8 object-contain shrink-0"
              />
              <div>
                <div className="text-sm font-medium">
                  {dt.label}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {dt.description}
                </div>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Configured Destinations */}
      <section>
        <h3 className="text-sm font-semibold mb-3">
          Configured Export Destinations
        </h3>
        {destinations.isLoading ? (
          <div className={cn(
            "flex items-center gap-2 text-sm",
            "text-muted-foreground py-4"
          )}>
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading destinations...
          </div>
        ) : (
          <div className="space-y-3">
            {allDests.length ? (
              allDests.map((dest: any) => (
                <DestinationCard
                  key={dest.id}
                  destination={dest}
                  onClick={() =>
                    navigate({
                      to: "/dashboard/settings",
                      search: { section: "destinations", destinationId: dest.id, provider: "" },
                    })
                  }
                />
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <p className="text-sm">No destinations configured</p>
                <p className="text-xs mt-1">Add a destination above to start exporting data.</p>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Dialog — used only for the "Add Destination" flow. Editing existing
          destinations happens from the detail view. */}
      {dialogType && (
        <AddDestinationDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          type={dialogType}
          formDrafts={formDrafts}
          setFormDrafts={setFormDrafts}
          onSaveSuccess={clearDraftAndClose}
        />
      )}
    </div>
  );
}
